import { useMemo, useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Skeleton,
  SimpleGrid,
  Pagination,
  Modal,
  TextInput,
  NumberInput,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCurrencyDollar,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconArrowsExchange,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { useCurrencyRates, useConvertCurrency } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

type SortField = 'from' | 'to' | 'rate' | 'effective';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function CurrencyRates() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('effective');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [convertOpened, { open: openConvert, close: closeConvert }] = useDisclosure(false);

  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('EUR');
  const [convertAmount, setConvertAmount] = useState(100);
  const [convertResult, setConvertResult] = useState<{ result: number; rate: number } | null>(null);

  const { data: rates, isLoading } = useCurrencyRates();
  const convertMutation = useConvertCurrency();

  const allRates = rates ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allRates;
    return [...allRates].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'from') cmp = a.from.localeCompare(b.from);
      else if (sortField === 'to') cmp = a.to.localeCompare(b.to);
      else if (sortField === 'rate') cmp = a.rate - b.rate;
      else cmp = dayjs(a.effective).unix() - dayjs(b.effective).unix();
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allRates, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRates = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const uniquePairs = new Set(allRates.map((r) => `${r.from}/${r.to}`));

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const handleConvert = () => {
    convertMutation.mutate(
      { from_currency: convertFrom, to_currency: convertTo, amount: convertAmount },
      {
        onSuccess: (res) => {
          setConvertResult(res.data);
        },
      },
    );
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Currency Rates"
        description="Exchange rates and currency conversion"
        actions={<Button leftSection={<IconArrowsExchange size={16} />} variant="light" onClick={openConvert}>Convert</Button>}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard title="Total Rates" value={String(allRates.length)} icon={<IconCurrencyDollar size={24} />} />
        <StatCard title="Currency Pairs" value={String(uniquePairs.size)} icon={<IconCurrencyDollar size={24} />} />
        <StatCard title="Last Updated" value={allRates.length > 0 ? dayjs(allRates[0].effective).format('MMM D') : 'N/A'} icon={<IconCurrencyDollar size={24} />} />
      </SimpleGrid>

      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : pageRates.length === 0 ? (
        <EmptyState icon={<IconCurrencyDollar size={30} />} title="No currency rates" description="No exchange rates have been configured yet" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <ThSort field="from">From</ThSort>
                  <ThSort field="to">To</ThSort>
                  <ThSort field="rate"><Text ta="right" inherit>Rate</Text></ThSort>
                  <ThSort field="effective">Effective Date</ThSort>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRates.map((rate, idx) => (
                  <Table.Tr key={`${rate.from}-${rate.to}-${rate.effective}-${idx}`}>
                    <Table.Td><Text size="sm" fw={500}>{rate.from}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{rate.to}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={600}>{rate.rate.toFixed(6)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{dayjs(rate.effective).format('MMM D, YYYY')}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}

      {/* Convert Modal */}
      <Modal opened={convertOpened} onClose={() => { closeConvert(); setConvertResult(null); }} title={<Text fw={600}>Currency Converter</Text>}>
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <TextInput label="From Currency" value={convertFrom} onChange={(e) => setConvertFrom(e.currentTarget.value.toUpperCase())} maxLength={3} />
            <TextInput label="To Currency" value={convertTo} onChange={(e) => setConvertTo(e.currentTarget.value.toUpperCase())} maxLength={3} />
          </SimpleGrid>
          <NumberInput label="Amount" value={convertAmount} onChange={(v) => setConvertAmount(Number(v) || 0)} min={0} />
          <Button onClick={handleConvert} loading={convertMutation.isPending}>Convert</Button>
          {convertResult && (
            <Paper p="md" bg="green.0">
              <Text size="lg" fw={700} ta="center">
                {convertAmount.toFixed(2)} {convertFrom} = {convertResult.result.toFixed(2)} {convertTo}
              </Text>
              <Text size="xs" c="dimmed" ta="center">Rate: {convertResult.rate.toFixed(6)}</Text>
            </Paper>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
